import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { fetchPublicCatalogue, type CataloguePlan } from '@/features/billing/api/billing';
import { billingCycleLabel } from '@/features/billing/lib/features';
import {
  clearReferralCode,
  referralCodeFrom,
  rememberBusinessName,
  selectedPlanFrom,
  subscribePathFor,
} from '@/features/billing/lib/planSelection';
import { minorToDecimalString } from '@/features/platform/api/platform';
import { PolicyConsentField } from '@/features/legal/components/PolicyConsentField';
import { useSignupPolicies } from '@/features/legal/hooks/useSignupPolicies';
import { checkReferralCode, type ReferralCheck } from '@/features/referrals/api/referrals';
import { trackEvent } from '@/lib/analytics';
import { useT } from '@/lib/i18n';
import { formatMoney } from '@/lib/money';

import { register as registerAccount, type RegisterPayload } from '../api/auth';
import type { ReferralPayload, SocialSession } from '../api/socialAuth';
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter';
import { SocialAuth } from '../components/social/SocialAuth';
import { useAuth } from '../hooks/useAuth';
import { MIN_PASSWORD_LENGTH } from '../lib/passwordStrength';
import { holdPendingSignup } from '../lib/pendingSignup';

// Mirrors the server-side policy in `app/schemas/auth.py`; the server remains
// the authority, this is only here to fail fast for the person typing.
function registerSchema(t: ReturnType<typeof useT>) {
  return z.object({
    full_name: z.string().min(2, t('auth.common.name_required')),
    // Asked for here, not on a later screen, because the storefront address is
    // reserved from it the moment the business is created and cannot be changed
    // afterwards - and the business has to exist before a subscription can.
    business_name: z
      .string()
      .min(2, t('auth.register.business_required'))
      .max(160, t('auth.common.too_long')),
    email: z.string().min(1, t('auth.common.email_required')).email(t('auth.common.email_invalid')),
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, t('auth.common.min_length', { min: MIN_PASSWORD_LENGTH }))
      .max(128, t('auth.common.too_long'))
      .refine((value) => /[A-Z]/.test(value), t('auth.register.rule_upper'))
      .refine((value) => /[a-z]/.test(value), t('auth.register.rule_lower'))
      .refine((value) => /[0-9]/.test(value), t('auth.register.rule_number'))
      .refine((value) => /[^A-Za-z0-9]/.test(value), t('auth.register.rule_special'))
      .refine((value) => new Set(value).size >= 5, t('auth.common.too_repetitive')),
    // Codes are handed out in capitals and read off posters and voice notes, so
    // whatever case it arrives in, it is one code.
    referral_code: z
      .string()
      .max(32, t('auth.register.code_too_long'))
      .trim()
      .toUpperCase()
      .optional(),
  });
}

type FormValues = z.infer<ReturnType<typeof registerSchema>>;

/** Long enough to stop asking mid-word, short enough to feel immediate. */
const REFERRAL_CHECK_DELAY_MS = 400;

function ReferralNotice({ checking, check }: { checking: boolean; check: ReferralCheck | null }) {
  const t = useT();
  if (checking) {
    return <p className="mt-1.5 text-xs text-muted">{t('auth.register.code_checking')}</p>;
  }

  if (!check) return null;

  if (!check.valid) {
    // Deliberately not styled as a field error. Nothing is broken - the account
    // can be created without a code - so this is a note, not a red stop sign.
    return (
      <p className="mt-1.5 text-sm text-amber-700">
        {check.message ?? t('auth.register.code_unknown')}
      </p>
    );
  }

  return (
    <div className="mt-2 flex items-start gap-2.5 rounded-xl border border-brand-200 bg-brand-50 px-3.5 py-3 text-sm text-brand-800">
      <BadgeCheck className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden />
      <div>
        <p className="font-semibold">
          {check.partner_name
            ? t('auth.register.code_referred_by', { partner: check.partner_name })
            : t('auth.register.code_accepted')}
        </p>
        {/* A code with no benefit still tracks the introduction, so say that
            rather than leaving a blank line where a discount should be. */}
        <p className="mt-0.5">
          {check.benefit ?? t('auth.register.code_no_benefit')}
        </p>
      </div>
    </div>
  );
}

/**
 * The plan chosen on the pricing page, named at the top of the form.
 *
 * Read back from the catalogue by its code rather than carried over from the
 * pricing page: the price, cycle and trial are whatever the server says they
 * are at this moment, and the same server will read them again from the plan
 * row when the subscription is created. Nothing shown here is trusted for
 * anything - it is the merchant's reassurance that the right plan is waiting.
 */
function SelectedPlanNotice({ plan }: { plan: CataloguePlan }) {
  const t = useT();
  const cycle = billingCycleLabel(plan.billing_interval, plan.billing_interval_count);
  const free = plan.price_minor === 0;
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-xl border border-brand-200 bg-brand-50 px-3.5 py-3 text-sm text-brand-800"
    >
      <Sparkles className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">
          {t('auth.register.plan_picked', { plan: plan.name })}
          {free ? '' : ` — ${formatMoney(minorToDecimalString(plan.price_minor))} / ${cycle}`}
        </p>
        <p className="mt-0.5">
          {plan.trial_days > 0
            ? t('auth.register.plan_trial', { days: plan.trial_days })
            : free
              ? t('auth.register.plan_free')
              : t('auth.register.plan_paid')}
        </p>
        <Link to="/pricing" className="mt-1 inline-block text-xs font-semibold underline">
          {t('auth.register.change_plan')}
        </Link>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const t = useT();
  const schema = useMemo(() => registerSchema(t), [t]);
  const navigate = useNavigate();
  const { reload } = useAuth();
  const [searchParams] = useSearchParams();
  const [formError, setFormError] = useState<string | null>(null);
  const [referral, setReferral] = useState<ReferralCheck | null>(null);
  const [checkingReferral, setCheckingReferral] = useState(false);
  // NeonCarts' own terms. Which ones, and at which version, come from the API;
  // the box is never ticked for them.
  const signupPolicies = useSignupPolicies();
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);

  // The code the link carried, if any. Kept so a code that was clicked can be
  // told apart from one that was typed, even though both end up in the box.
  //
  // Falls back to what an earlier page remembered, which is still a code that
  // arrived on a link - the pricing page it landed on is simply not this one.
  // `LINK` below therefore stays true across an email round trip or a new tab,
  // where the query string does not survive.
  //
  // Read once, on arrival: `referralCodeFrom` remembers what it finds, and a
  // read on every render would keep writing the code back after the successful
  // registration below has deliberately cleared it.
  const [linkCode] = useState(() => referralCodeFrom(searchParams) ?? '');

  // Most people signing up have no code, so the box starts folded away behind a
  // link and only the few who were referred ever see it. Someone who arrived on
  // a referral link already has one filled in, so for them it opens on arrival -
  // a prefilled field nobody can see would look like the code was dropped.
  const [showReferralField, setShowReferralField] = useState(() => linkCode !== '');
  // Set only when the link is clicked, so opening the field moves the cursor
  // into it, while opening it for a link code leaves focus at the top of the form.
  const focusReferralField = useRef(false);

  // The plan the pricing page sent them with. Remembered across the rest of
  // sign-up - verification, sign-in, creating the business - and waiting at
  // checkout on the far side of it.
  const selectedPlanCode = selectedPlanFrom(searchParams);
  const catalogue = useQuery({
    queryKey: ['public', 'pricing-catalogue'],
    queryFn: fetchPublicCatalogue,
    staleTime: 5 * 60 * 1000,
    enabled: selectedPlanCode !== null,
  });
  const selectedPlan =
    catalogue.data?.plans.find((plan) => plan.code === selectedPlanCode) ?? null;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    // Carried over from the landing page form, if they started there.
    defaultValues: { email: searchParams.get('email') ?? '', referral_code: linkCode },
  });

  // Split out so the field can keep react-hook-form's ref and still be focused
  // the moment it is revealed.
  const { ref: referralFieldRef, ...referralField } = register('referral_code');

  const typedCode = (watch('referral_code') ?? '').trim().toUpperCase();
  const typedPassword = watch('password') ?? '';

  /**
   * Check the code while they type rather than on submit.
   *
   * The discount is the reason a referred merchant is on this page at all;
   * finding out it never applied - after paying - is far too late. The server
   * checks it again at registration and has the final word, so this is
   * reassurance, not validation.
   */
  useEffect(() => {
    if (!typedCode) {
      setReferral(null);
      setCheckingReferral(false);
      return;
    }

    let cancelled = false;
    setCheckingReferral(true);

    const timer = setTimeout(() => {
      checkReferralCode(typedCode)
        .then((result) => {
          if (!cancelled) setReferral(result);
        })
        .catch(() => {
          // A check we could not make is not a code that is wrong, so it says
          // nothing rather than accusing the merchant of a typo.
          if (!cancelled) setReferral(null);
        })
        .finally(() => {
          if (!cancelled) setCheckingReferral(false);
        });
    }, REFERRAL_CHECK_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [typedCode]);

  // The code in the box, if any, rides along with Google or WhatsApp exactly as
  // it would with the form - it only counts if they turn out to be new.
  const socialReferral: ReferralPayload | undefined = typedCode
    ? { referral_code: typedCode, referral_source: typedCode === linkCode ? 'LINK' : 'FORM' }
    : undefined;

  // A new account has no business yet, and onboarding is where one is made -
  // it carries on to the plan picked on the pricing page. Somebody who already
  // had an account has simply signed in.
  const onSocialSignIn = async (session: SocialSession) => {
    if (session.is_new_account) {
      clearReferralCode();
      trackEvent('sign_up', { method: 'social' });
    }
    await reload();
    if (session.is_new_account) {
      void navigate('/onboarding', { replace: true });
      return;
    }
    void navigate(selectedPlanCode ? subscribePathFor(selectedPlanCode) : '/app', {
      replace: true,
    });
  };

  const onSubmit = async (values: FormValues) => {
    setFormError(null);
    setPolicyError(null);
    // Checked here as well as on the server so the person is told beside the
    // box, before a round trip, rather than at the top of the form.
    if (signupPolicies.required.length > 0 && !policiesAccepted) {
      setPolicyError(t('auth.register.policies_required'));
      return;
    }
    // Kept before anything can fail, so a merchant who has to verify their
    // email - or who comes back after a stumble - is not asked for it twice.
    rememberBusinessName(values.business_name);

    const code = (values.referral_code ?? '').trim().toUpperCase();
    const payload: RegisterPayload = {
      full_name: values.full_name,
      email: values.email,
      password: values.password,
    };
    if (code) {
      payload.referral_code = code;
      payload.referral_source = code === linkCode ? 'LINK' : 'FORM';
    }
    if (policiesAccepted && signupPolicies.required.length > 0) {
      payload.accepted_policy_version_ids = signupPolicies.required.map(
        (policy) => policy.version_id,
      );
    }

    try {
      const result = await registerAccount(payload);
      trackEvent('sign_up', { method: 'email' });

      // Redeemed, or refused and not worth carrying: either way the code has
      // done its work and should not follow this browser into a later sign-up.
      clearReferralCode();

      // The account exists but is not active yet, and will not be until the
      // code comes back - so straight to the screen that asks for it. The
      // server has already answered; the email itself goes out after the
      // response, so nothing here waits on a mail server.
      //
      // Deliberately unconditional, and *not* keyed on
      // `requires_email_verification`. That flag answers a different question -
      // whether signing in is refused while an address is unproven - and
      // registration emails a code either way. Reading it here is what let a
      // deployment with the flag off take a merchant from the form to the
      // payment screen without ever asking for the code it had just sent them.
      //
      // A route, not a step inside this page, so a reload keeps them on the
      // code box. Inline, a reload dropped them back on an empty form for an
      // account that already existed, and submitting it again answered
      // EMAIL_ALREADY_REGISTERED. The password rides across in memory only -
      // see `pendingSignup` - so the verify page can sign them straight in.
      holdPendingSignup(values.email, values.password);
      void navigate(`/verify-email?email=${encodeURIComponent(values.email)}`, {
        state: { fromSignup: true, resendAfter: result.code_resend_after },
      });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'REFERRAL_CODE_INVALID') {
        // Repeat the refusal beside the box that has to change, not only at the
        // top of a form they have already scrolled past.
        setReferral({
          code,
          valid: false,
          partner_name: null,
          benefit: null,
          reason: typeof error.details?.reason === 'string' ? error.details.reason : null,
          message: error.message,
        });
      }

      if (
        error instanceof ApiError &&
        (error.code === 'POLICY_ACCEPTANCE_REQUIRED' || error.code === 'POLICY_VERSION_NOT_CURRENT')
      ) {
        // A policy was published or updated while the form was open. Show the
        // current list and ask again, rather than ticking a box for words the
        // person has not seen.
        setPoliciesAccepted(false);
        void signupPolicies.refetch();
        setPolicyError(
          error.code === 'POLICY_VERSION_NOT_CURRENT'
            ? t('auth.register.policies_updated')
            : error.message,
        );
        return;
      }

      setFormError(
        error instanceof ApiError ? error.message : t('auth.common.generic_error'),
      );
    }
  };

  return (
    // `data-auth-wide` lets AuthLayout give this form a two-column card, so the
    // whole of sign-up fits on one screen without scrolling.
    <div data-auth-wide>
      <h1 className="text-2xl font-bold tracking-tight text-ink">{t('auth.register.title')}</h1>
      <p className="mt-1.5 text-sm text-muted">
        <span>
          {selectedPlan && selectedPlan.price_minor > 0
            ? selectedPlan.trial_days > 0
              ? t('auth.register.intro_trial', { days: selectedPlan.trial_days })
              : t('auth.register.intro_paid')
            : t('auth.register.intro_free')}
        </span>{' '}
        {t('auth.register.have_account')}{' '}
        <Link to="/login" className="font-semibold text-brand-600 hover:underline">
          {t('auth.common.sign_in')}
        </Link>
      </p>

      {selectedPlan ? (
        <div className="mt-5">
          <SelectedPlanNotice plan={selectedPlan} />
        </div>
      ) : null}

      {/* Above every way of signing up, not inside the email form, so a code
          counts for Google and WhatsApp sign-ups too - they send it with
          `socialReferral` below. */}
      <div className="mt-5">
        {showReferralField ? (
          <div>
            <TextField
              label={t('auth.register.code_label')}
              placeholder="ASHA10"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="uppercase"
              hint={
                referral || checkingReferral
                  ? undefined
                  : t('auth.register.code_hint')
              }
              error={errors.referral_code?.message}
              {...referralField}
              ref={(node) => {
                referralFieldRef(node);
                if (node && focusReferralField.current) {
                  focusReferralField.current = false;
                  node.focus();
                }
              }}
            />
            <div aria-live="polite">
              <ReferralNotice checking={checkingReferral} check={referral} />
            </div>
            {typedCode ? null : (
              <Link to="/deals" className="mt-1 inline-block text-xs font-semibold text-brand-600 hover:underline">
                {t('auth.register.deals_link')}
              </Link>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <button
              type="button"
              onClick={() => {
                focusReferralField.current = true;
                setShowReferralField(true);
              }}
              className="text-sm font-semibold text-brand-600 hover:underline"
            >
              {t('auth.register.code_toggle')}
            </button>
            <Link to="/deals" className="text-sm text-muted hover:text-brand-600 hover:underline">
              {t('auth.register.deals_link')}
            </Link>
          </div>
        )}
      </div>

      <div className="mt-5">
        <SocialAuth mode="register" referral={socialReferral} onAuthenticated={onSocialSignIn}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {formError ? <Alert tone="error">{formError}</Alert> : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label={t('auth.common.full_name')}
                autoComplete="name"
                placeholder="Asha Rao"
                error={errors.full_name?.message}
                {...register('full_name')}
              />
              <TextField
                label={t('auth.register.business_name')}
                autoComplete="organization"
                placeholder="Neon Threads"
                info={t('auth.register.business_name_info')}
                error={errors.business_name?.message}
                {...register('business_name')}
              />
              <TextField
                label={t('auth.common.email')}
                type="email"
                autoComplete="email"
                placeholder="you@business.in"
                error={errors.email?.message}
                {...register('email')}
              />
              <div>
                <TextField
                  label={t('auth.common.password')}
                  type="password"
                  autoComplete="new-password"
                  error={errors.password?.message}
                  {...register('password')}
                />
                <PasswordStrengthMeter value={typedPassword} />
              </div>
            </div>

            <PolicyConsentField
              policies={signupPolicies.required}
              checked={policiesAccepted}
              onChange={(checked) => {
                setPoliciesAccepted(checked);
                if (checked) setPolicyError(null);
              }}
              error={policyError}
            />

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('auth.register.submitting') : t('auth.register.submit')}
            </Button>
          </form>
        </SocialAuth>
      </div>
    </div>
  );
}
